import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  Pressable,
  TextInput,
  ScrollView,
  Image,
  ActivityIndicator,
} from 'react-native';
import { useMutation } from '@tanstack/react-query';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { MailCheck, RefreshCw, AtSign } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { useAlert } from '@/components/AlertDialog';
import { AuthLayout } from '@/components/AuthLayout';
import { AppModal } from '@/components/modal';
import { ModalAction } from '@/components/modal/ModalAction';
import { isWeb } from '@/lib/platform';
import { useAuth, supabase } from '@/lib/supabase';
import { handleError } from '@/lib/errors';
import AnimatedPressable from '@/components/AnimatedPressable';
import { LOGO_MARK } from '@/lib/logo';

const RESEND_COOLDOWN_SECONDS = 60;

// Basic email shape check — server remains the source of truth.
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export default function ConfirmEmailScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const { alert, error: showError, success: showSuccess } = useAlert();
  const { user, resendConfirmationEmail, changeEmail, signOut } = useAuth();

  const currentEmail = user?.email ?? '';

  const [cooldown, setCooldown] = useState(0);
  const [changeModalVisible, setChangeModalVisible] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  // Countdown ticker for the resend cooldown.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const resendMutation = useMutation({
    mutationFn: async () => {
      const { error } = await resendConfirmationEmail(currentEmail);
      if (error) throw error;
    },
    onSuccess: () => {
      setCooldown(RESEND_COOLDOWN_SECONDS);
      showSuccess('Email sent', `We've re-sent a confirmation link to ${currentEmail}.`);
    },
    onError: (err) => {
      const { message, isCancelled } = handleError(err, { context: 'ConfirmEmail.resend' });
      if (!isCancelled) showError('Could not resend', message);
    },
  });

  const changeEmailMutation = useMutation({
    mutationFn: async (email: string) => {
      const { error } = await changeEmail(email);
      if (error) throw error;
    },
    onSuccess: (_data, email) => {
      setChangeModalVisible(false);
      setNewEmail('');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      showSuccess('Check your inbox', `We've sent a confirmation link to ${email}.`);
    },
    onError: (err) => {
      const { message, isCancelled } = handleError(err, { context: 'ConfirmEmail.changeEmail' });
      if (!isCancelled) showError('Could not change email', message);
    },
  });

  // "I've confirmed" — re-pull the session so a freshly-set email_confirmed_at is
  // picked up (the auth listener updates state.user), then bounce to index which
  // re-evaluates the access gate. Honest: if it's still unconfirmed the gate keeps
  // the user here rather than faking success.
  const refreshMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      return !!data.user?.email_confirmed_at;
    },
    onSuccess: (confirmed) => {
      if (confirmed) {
        router.replace('/');
      } else {
        alert('Not confirmed yet', "We couldn't confirm your email yet. Please click the link in the email, then try again.");
      }
    },
    onError: (err) => {
      const { message, isCancelled } = handleError(err, { context: 'ConfirmEmail.refresh' });
      if (!isCancelled) showError('Check failed', message);
    },
  });

  const handleBackToLogin = useCallback(async () => {
    try {
      await signOut();
    } catch (err) {
      handleError(err, { context: 'ConfirmEmail.backToLogin', silent: true });
    }
    router.replace('/login');
  }, [signOut, router]);

  const handleSubmitNewEmail = () => {
    const trimmed = newEmail.trim();
    if (!isValidEmail(trimmed)) {
      alert('Invalid email', 'Please enter a valid email address.');
      return;
    }
    if (trimmed.toLowerCase() === currentEmail.toLowerCase()) {
      alert('Same email', 'That is already your current email address.');
      return;
    }
    changeEmailMutation.mutate(trimmed);
  };

  const resendDisabled = resendMutation.isPending || cooldown > 0 || !currentEmail;
  const resendLabel = resendMutation.isPending
    ? 'Sending...'
    : cooldown > 0
      ? `Resend in ${cooldown}s`
      : 'Resend confirmation email';

  return (
    <AuthLayout>
      <View className="flex-1" style={{ backgroundColor: isWeb ? colors.surface.primary : colors.surface.secondary }}>
        <SafeAreaView edges={isWeb ? [] : ['top', 'bottom']} className="flex-1">
          <ScrollView
            className="flex-1"
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ flexGrow: 1, justifyContent: isWeb ? 'center' : 'flex-start' }}
            keyboardShouldPersistTaps="handled"
          >
            {/* Icon + title */}
            <Animated.View
              entering={FadeInDown.delay(100).duration(500)}
              className={`items-center px-5 ${isWeb ? 'pt-4 pb-6' : 'pt-12 pb-8'}`}
            >
              {!isWeb && (
                <Image source={LOGO_MARK} className="w-16 h-16 mb-6" resizeMode="contain" accessibilityLabel="Inverio logo" />
              )}
              <View
                className="w-24 h-24 rounded-full items-center justify-center mb-6"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <MailCheck size={44} color={colors.primary[600]} />
              </View>
              <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mb-3 text-center">
                Confirm your email
              </Text>
              <Text style={{ color: colors.text.secondary }} className="text-center text-base leading-relaxed px-4">
                We sent a confirmation link to
              </Text>
              <Text
                numberOfLines={1}
                style={{ color: colors.text.primary }}
                className="text-center text-base font-semibold mt-1 px-4"
              >
                {currentEmail || 'your email address'}
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-center text-sm leading-relaxed px-6 mt-3">
                Click the link in that email to activate your account. Once confirmed, you can continue into Inverio.
              </Text>
            </Animated.View>

            {/* Primary: I've confirmed */}
            <Animated.View entering={FadeInUp.delay(200).duration(500)} className="px-5">
              <AnimatedPressable
                onPress={() => refreshMutation.mutate()}
                disabled={refreshMutation.isPending}
                accessibilityRole="button"
                accessibilityLabel="I have confirmed my email"
                className="rounded-xl flex-row items-center justify-center gap-2"
                style={{ minHeight: 52, backgroundColor: colors.primary[600], opacity: refreshMutation.isPending ? 0.7 : 1 }}
              >
                {refreshMutation.isPending && <ActivityIndicator size="small" color={colors.text.inverse} />}
                <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                  {refreshMutation.isPending ? 'Checking...' : "I've confirmed my email"}
                </Text>
              </AnimatedPressable>
            </Animated.View>

            {/* Secondary: Resend */}
            <Animated.View entering={FadeInUp.delay(300).duration(500)} className="px-5 mt-3">
              <AnimatedPressable
                onPress={() => resendMutation.mutate()}
                disabled={resendDisabled}
                accessibilityRole="button"
                accessibilityLabel="Resend confirmation email"
                className="rounded-xl flex-row items-center justify-center gap-2"
                style={{
                  minHeight: 52,
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.border.medium,
                  opacity: resendDisabled ? 0.6 : 1,
                }}
              >
                <RefreshCw size={18} color={colors.primary[600]} />
                <Text style={{ color: colors.primary[600] }} className="font-semibold text-base">
                  {resendLabel}
                </Text>
              </AnimatedPressable>
            </Animated.View>

            {/* Tertiary: Change email */}
            <Animated.View entering={FadeInUp.delay(400).duration(500)} className="px-5 mt-3">
              <AnimatedPressable
                onPress={() => setChangeModalVisible(true)}
                accessibilityRole="button"
                accessibilityLabel="Change email address"
                className="rounded-xl flex-row items-center justify-center gap-2"
                style={{ minHeight: 52 }}
              >
                <AtSign size={18} color={colors.text.secondary} />
                <Text style={{ color: colors.text.secondary }} className="font-medium text-base">
                  Use a different email
                </Text>
              </AnimatedPressable>
            </Animated.View>

            {/* Back to sign in */}
            <Animated.View entering={FadeInUp.delay(500).duration(500)} className="items-center px-5 mt-6 pb-8">
              <Pressable
                onPress={handleBackToLogin}
                accessibilityRole="button"
                accessibilityLabel="Back to sign in"
                className="py-3 px-4"
                style={{ minHeight: 44, justifyContent: 'center' }}
              >
                <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                  Back to sign in
                </Text>
              </Pressable>
            </Animated.View>
          </ScrollView>
        </SafeAreaView>
      </View>

      {/* Change-email modal */}
      <AppModal
        visible={changeModalVisible}
        onClose={() => setChangeModalVisible(false)}
        title="Change email address"
        maxWidth={460}
        footer={
          <AppModal.Actions>
            <ModalAction
              label="Cancel"
              variant="neutral"
              onPress={() => setChangeModalVisible(false)}
              disabled={changeEmailMutation.isPending}
            />
            <ModalAction
              label={changeEmailMutation.isPending ? 'Sending...' : 'Send link'}
              variant="primary"
              onPress={handleSubmitNewEmail}
              disabled={changeEmailMutation.isPending}
            />
          </AppModal.Actions>
        }
      >
        <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed mb-4">
          Enter the email you want to use. We'll send a new confirmation link there. You'll stay signed out of the app until it's confirmed.
        </Text>
        <View
          className="flex-row items-center rounded-xl px-4"
          style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}
        >
          <AtSign size={20} color={colors.text.tertiary} />
          <TextInput
            style={{ color: colors.text.primary }}
            className="flex-1 py-4 pl-3 text-base"
            placeholder="you@company.com"
            placeholderTextColor={colors.text.tertiary}
            value={newEmail}
            onChangeText={setNewEmail}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="email-address"
            editable={!changeEmailMutation.isPending}
            accessibilityLabel="New email address"
          />
        </View>
      </AppModal>
    </AuthLayout>
  );
}
